import { Star } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { Avatar, Rating } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { GovIdVerifiedBadge } from '@src/components/common/gov-id-verified-badge/gov-id-verified-badge';
import { useExpandableTruncation } from '@src/components/hooks/useExpandableTruncation';
import { Button } from '@src/components/ui/button/button';
import { Form } from '@src/components/ui/react-hook-form/form/form';
import { SubmitButton } from '@src/components/ui/react-hook-form/submit-button/submit-button';
import { formatDateTime } from '@src/utils/date/formatDateTime';
import { cn } from '@src/utils/ui';
import React, { useRef } from 'react';

import { useWatch } from 'react-hook-form';

export const Reviews = ({ feedback }) => {
    const handleSubmit = ({ reviews }, { setValue }) => {
        setValue('reviews', feedback.itemReviews.slice(0, reviews.length + 5));
    };

    return (
        <Form
            className='ds-description tw-space-y-[8px]'
            onSubmit={handleSubmit}
            defaultValues={{ reviews: feedback.itemReviews.slice(0, 5) }}
        >
            <div className='tw-flex tw-gap-[8px] tw-items-start tw-flex-col md:tw-items-center  md:tw-flex-row'>
                <div className='tw-flex tw-gap-[8px] tw-items-center'>
                    <h6 className='tw-gap-[12px] md:tw-text-[#0050C4] tw-font-[700] tw-text-[20px] tw-leading-[160%] tw-tracking-[0.15px]'>
                        Reviews
                    </h6>
                    {feedback.ratingsCount > 0 ? (
                        <div className='tw-space-x-[4px]'>
                            <Star size={16} weight='fill' className='tw-text-yellow-500' />
                            <span className='tw-font-[500] tw-tracking-[0.46px] tw-leading-[22px] tw-align-middle tw-text-[13px]'>
                                {feedback.ratings} ({feedback.ratingsCount})
                            </span>
                        </div>
                    ) : null}
                </div>
            </div>
            <ReviewList />
            <Buttons feedback={feedback} />
        </Form>
    );
};

const ReviewList = () => {
    const reviews = useWatch({ name: 'reviews' });

    if (reviews.length < 1) {
        return (
            <div className='empty-page-sec'>
                <img src='/assets/images/chats-circle.svg' className='tw-m-auto' alt='empty reviews' />
                <div className='empty-page-title'>No reviews yet</div>
                <div className='empty-page-message'>Be the first one to share your experience and help others.</div>
            </div>
        );
    }

    return (
        <div className='tw-space-y-[16px]'>
            {reviews.map(review => (
                <ReviewCard key={review.id} {...review} />
            ))}
        </div>
    );
};

function ReviewCard({ customerName, customerRating, ratingCreated, ratingMessage, mediaUrl, isGovIdVerified }) {
    const descRef = useRef(null);
    const { isTruncated, isExpanded, toggleExpand } = useExpandableTruncation(descRef);

    return (
        <div className='tw-space-y-[4px] tw-bg-[#ECEFF1] tw-border tw-border-[rgba(0, 0, 0, 0.12)] tw-p-[16px]'>
            <div className='tw-flex tw-gap-[8px]'>
                <Avatar src={mediaUrl} />
                <div className='tw-space-y-[16px] tw-grow tw-w-[1px]'>
                    <div className='tw-flex tw-gap-[8px] tw-items-center'>
                        <div>
                            <div className='tw-font-medium'>{customerName}</div>
                            <div className='tw-text-[#5A5A5A] tw-text-[14px]'>
                                {formatDateTime(ratingCreated, 'MM/DD/YYYY')}
                            </div>
                        </div>
                        {isGovIdVerified ? <GovIdVerifiedBadge className='tw-w-[24px] tw-aspect-square' /> : null}
                        <div className='tw-self-stretch tw-grow tw-flex tw-justify-end tw-items-start'>
                            <Rating name='tdp-rating' value={customerRating} readOnly />
                        </div>
                    </div>
                    <div className='tw-space-y-[4px]'>
                        <div
                            ref={descRef}
                            className={cn(
                                'tw-text-[16px] tw-leading-[24px] tw-tracking-[0] tw-text-[#5A5A5A] tw-transition-all tw-break-words',
                                !isExpanded && 'tw-line-clamp-4',
                                'tw-whitespace-normal tw-break-words'
                            )}
                        >
                            {ratingMessage}
                        </div>
                    </div>
                </div>
            </div>
            {isTruncated && (
                <div className='tw-flex tw-justify-center tw-w-full'>
                    <Button
                        variant='text'
                        onClick={toggleExpand}
                        className='tw-text-[#5A5A5A] !tw-text-[14px] !tw-capitalize'
                        startIcon={
                            <KeyboardArrowDownIcon
                                size={24}
                                className='tw-transition-transform'
                                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}
                            />
                        }
                    >
                        {isExpanded ? 'Show Less' : 'Show More'}
                    </Button>
                </div>
            )}
        </div>
    );
}

function Buttons({ feedback }) {
    const reviews = useWatch({ name: 'reviews' });

    const handleScrollTop = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        });
    };

    return (
        <div className='tw-pt-[16px] tw-flex tw-flex-col tw-gap-[8px] tw-items-center'>
            {feedback.ratingsCount <= reviews.length ? null : (
                <SubmitButton variant='contained'>SHOW MORE REVIEWS</SubmitButton>
            )}
            <Button variant='text' onClick={handleScrollTop}>
                BACK TO TOP
            </Button>
        </div>
    );
}

ReviewCard.propTypes = {
    customerName: PropTypes.string,
    customerRating: PropTypes.number,
    ratingCreated: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    ratingMessage: PropTypes.string,
    mediaUrl: PropTypes.string,
    isGovIdVerified: PropTypes.bool
};

Reviews.propTypes = {
    feedback: PropTypes.shape({
        ratings: PropTypes.number.isRequired,
        ratingsCount: PropTypes.number.isRequired,
        itemReviews: PropTypes.arrayOf(PropTypes.shape(ReviewCard.propTypes)).isRequired
    }).isRequired
};

Buttons.propTypes = Reviews.propTypes;
